import React from "react";
import { Outlet } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { NavBar } from "@app.automotus.io/components/NavBar";
import Box from "@mui/material/Box";
import { useLogout } from "@app.automotus.io/components/auth";
import { useIsMobile, useUserProfile } from "@app.automotus.io/components/hooks";
import { Theme } from "@mui/material/styles";
import { getNavigationConfig } from "../../utils/userProfile";
import { ZendeskWidgetVisibility } from "../../components/ZendeskWidgetVisibility";

export const MainLayout: React.FC = () => {
  const mainRef = React.useRef(null);
  const logout = useLogout();
  const { userProfile } = useUserProfile();
  const isMobile = useIsMobile();

  const NAV_LINKS = React.useMemo(() => {
    return getNavigationConfig(userProfile, isMobile, logout);
  }, [userProfile, isMobile, logout]);

  return (
    <>
      <CssBaseline />
      {/* Overrides App.tsx's default-hidden visibility for every authenticated route, since
       * this is the single element they all nest under (behind ProtectedComponent). Mounting
       * after App in the render tree is what lets this win the CSS cascade tie. Hidden again
       * on mobile, where there's no room for a floating launcher bubble. */}
      <ZendeskWidgetVisibility visible={!isMobile} />
      <NavBar
        targets={NAV_LINKS}
        target={mainRef}
        supportUrl={process.env.REACT_APP_CURBSUITE_ZENDESK_SUPPORT_TICKET_URL || ""}
        resourcesUrl={process.env.REACT_APP_CURBSUITE_SUPPORT_URL || ""}
      />
      <Box
        component="main"
        ref={mainRef}
        sx={{
          pt: (theme: Theme) => (isMobile ? theme.spacing(6.75) : theme.spacing(11)),
          // display:flex + height:100% (rather than relying on flexGrow alone, which only sizes
          // main itself within #root) — mirrors PermitsLayout's own wrapper so every route gets
          // the same definite-height flex chain down to its DataTableContainer, not just Permits.
          display: "flex",
          flexDirection: "column",
          height: "100%",
          flexGrow: 1,
          maxWidth: "100vw",
          overflowX: "auto",
        }}
      >
        <Outlet />
      </Box>
    </>
  );
};
